<template>
<v-layout column>
  <v-flex xs12 class="pa-4">
    <p class="details-title mb-2">{{
      $t("reservations.title", { entityType: isRoomService ? $t('activity_reservation.order') : $t('activity_reservation.reservation').toLowerCase() })
    }}</p>
    <v-layout>
      <v-tooltip bottom :disabled="!reservationTimeHasPassed" v-if="statusNotCompleted && editionAvailable" max-width="300">
        <template v-slot:activator="{ on, attrs }">
          <div v-bind="attrs" v-on="on">
            <v-btn :disabled="reservationTimeHasPassed" outlined color="primary" class="primary-gradient" @click="triggerEditionModalOpen = new Date()">
              <span class="px-2">{{$t('activity_reservation.actions.manage')}}</span>
            </v-btn>
          </div>
        </template>
        {{$t(`activity_reservation.edit.manage_past_limit.${reservationMethod.type}`)}}
      </v-tooltip>
      <v-spacer />
      <v-chip
        label
        :color="activityReservationColorsByStatus[item.reservation.status].background"
        :text-color="activityReservationColorsByStatus[item.reservation.status].text"
        @click="showTimeline ? showTimeline = false : showTimeline = true"
      >
        {{ isRoomService && $te("reservations.order_status." + item.reservation.status)
          ? $t("reservations.order_status." + item.reservation.status) : $t("reservations.status." + item.reservation.status)
        }}
        <v-icon right>{{ showTimeline ? 'arrow_drop_up' : 'arrow_drop_down' }}</v-icon>
      </v-chip>
    </v-layout>
    <reservation-timeline :show="showTimeline" :item="item" @hideTimeline="showTimeline = false"/>
    <v-divider class="mb-4 mt-4" />
    <span class="details-data-bold">{{
      $t(`activities.reservation_types.${item.reservation.type}`)
    }}</span>
    <v-text-field
      class="mt-4"
      v-if="item.reservation.guest && item.reservation.guest.profile"
      :label="$t('fields.in_whose_name')"
      outlined
      readonly
      hide-details
      :value="`${item.reservation.guest.profile.name} ${item.reservation.guest.profile.lastName || null}`"
    />
    <v-text-field
      class="mt-4"
      :label="$t('reservations.reservation_date')"
      outlined
      readonly
      hide-details
      :value="requestedOrAcceptedDate"
    />
    <v-text-field
      v-if="item.room"
      class="mt-4"
      :label="$t('fields.room')"
      outlined
      readonly
      hide-details
      :value="item.room.name"
    />
    <v-flex xs12 v-if="nominalAttendees.length" class="mt-4">
      <v-autocomplete
        :label="$t('fields.attendees')"
        :items="nominalAttendees"
        :value="nominalAttendees"
        return-object
        append-icon=""
        hide-details
        outlined
        readonly
        multiple
      >
        <template v-slot:selection="{ item }">
          <v-chip v-if="item.guest.profile">
            {{ item.guest.profile.name }} {{ item.guest.profile.lastName || null }}
          </v-chip>
        </template>
      </v-autocomplete>
    </v-flex>
    <v-flex xs12 v-if="numeralAttendees.length">
      <v-flex
        v-for="(attendee, i) in numeralAttendees"
        :key="i"
        xs12>
        <v-text-field
          :label="$t('activity_reservation.attendee_type', { type: attendee.attendeeTypeName })"
          :value="attendee.quantity"
          class="mt-4"
          hide-details
          readonly
          outlined />
      </v-flex>
    </v-flex>
    <v-text-field
      v-if="item.reservation.comments"
      class="mt-4"
      :label="$t('fields.comments')"
      outlined
      readonly
      hide-details
      :value="item.reservation.comments"
    />
    <v-text-field
      v-if="item.reservation.reason"
      class="mt-4"
      :label="$t('fields.reason')"
      outlined
      readonly
      hide-details
      :value="item.reservation.reason"
    />
  </v-flex>
  <v-spacer/>
  <v-layout column class="ma-4">
    <v-layout wrap>
      <v-flex v-if="ticketFileId" xs12 :sm6="refundTicketFileId" :class="{ 'pr-1': refundTicketFileId && $vuetify.breakpoint.smAndUp }">
        <v-btn
          @click="getTicket(ticketFileId)"
          outlined
          block
          color="primary"
          class="primary-gradient flex-none"
          >{{ $t("reservations.ticket") }}</v-btn
        >
      </v-flex>
      <v-flex v-if="refundTicketFileId" xs12 sm6 :class="ticketFileId && $vuetify.breakpoint.smAndUp ? 'pl-1' : 'mt-2'">
        <v-btn
          @click="getTicket(refundTicketFileId)"
          block
          outlined
          color="primary"
          class="primary-gradient flex-none"
          >{{ $t("reservations.refund_ticket") }}</v-btn
        >
      </v-flex>
    </v-layout>
    <v-btn v-if="order"
      @click="$emit('displayOrder')"
      color="primary"
      class="primary-gradient"
      :class="{ 'mt-2': ticketFileId || refundTicketFileId }"
      centered
      outlined
      block
    >{{ $t("activity_reservation.details") }}</v-btn>
  </v-layout>
  <edit-reservation-modal :item="item" :triggerOpen="triggerEditionModalOpen" @reload="reload" />
</v-layout>
</template>

<script>
import { mapState } from 'vuex'
import { activityReservationColorsByStatus, activityReservationStatus, orderLineItemEntities, orderLineTypes } from '@/config'
import moment from 'moment'

import { ActivityReservationsRequests } from '@/services/activityReservations.requests'
import reservationMixins from '@/mixins/Reservation'

const activityReservationsService = new ActivityReservationsRequests()

export default {
  components: {
    ReservationTimeline: () => import('@/components/view-components/reservations/ReservationTimeline.vue'),
    EditReservationModal: () => import('@/components/view-components/reservations/EditReservationModal.vue'),
  },
  props: {
    item: Object,
  },
  data () {
    return {
      moment,
      activityReservationStatus,
      activityReservationColorsByStatus,
      triggerEditionModalOpen: null,
      showTimeline: false,
    }
  },
  methods: {
    reload () {
      this.$emit('reload')
    },
    getTicket (ticketId) {
      activityReservationsService.getTicket(this.item.reservation.id, ticketId)
        .then(route => {
          window.open(route)
        })
        .catch(() => {})
    },
    goToOrder () {
      this.$emit('displayOrder')
    },
    getCurrencyById (id) {
      return this.currencies.find(c => {
        return c.id === id
      })
    },
  },
  computed: {
    ...mapState({
      activity: (state) => state.activities.extendedActivity,
    }),
    reservationMethod () {
      if (!this.activity) {
        return null
      }
      const rm = this.activity.reservationMethods.find((rM) => {
        return rM.reservationMethod.type === this.item.reservation.type
      })
      return rm ? rm.reservationMethod : null
    },
    editionAvailable () {
      return this.reservationMethod && (this.reservationMethod.minimumModificationTime !== null || this.reservationMethod.minimumCancellationTime !== null)
    },
    statusNotCompleted () {
      return this.item.reservation.status === activityReservationStatus.ACCEPTED || this.item.reservation.status === activityReservationStatus.PENDING || this.item.reservation.status === activityReservationStatus.PENDING_MODIFICATION
    },
    order () {
      return this.item.order || this.item.pendingOrder
    },
    ticketTemplateFileId () {
      return this.item.activity.ticketTemplateFileId
    },
    ticketFileId () {
      return this.item.reservation.ticketFileId
    },
    refundTicketFileId () {
      return this.item.reservation.refundTicketFileId
    },
    quantity () {
      if (!this.order) {
        return null
      }
      var quantity = 0
      this.order.orderLines.forEach(orderLine => {
        if (!orderLine.parentOrderLine && orderLine.orderLineType === orderLineTypes.SALE && orderLine.itemEntity === orderLineItemEntities.PRODUCT) {
          quantity += orderLine.quantity
        }
      })
      return quantity
    },
    nominalAttendees () {
      return this.item.nominalAttendees
    },
    numeralAttendees () {
      return this.item.numeralAttendees
    },
    isRoomService () {
      return this.item.reservation.type === 'ROOM_SERVICE'
    },
    currencies () {
      return this.item.currencies
    },
    requestedOrAcceptedDate () {
      if (this.item.reservation.acceptedDate) {
        return moment(this.item.reservation.acceptedDate).add(this.item.reservation.acceptedTime, 'ms').format('DD/MM/YYYY HH:mm')
      } else {
        return moment(this.item.reservation.requestedDate).add(this.item.reservation.requestedTime, 'ms').format('DD/MM/YYYY HH:mm')
      }
    },
    reservationTimeHasPassed () {
      return moment().isAfter(moment(this.item.reservation.acceptedDate || this.item.reservation.requestedDate).add(this.item.reservation.acceptedTime || this.item.reservation.requestedTime, 'ms'))
    },
  },
  watch: {
    item (newItem, oldItem) {
      this.showTimeline = false
    },
  },
  mixins: [
    reservationMixins,
  ],
}
</script>

<style lang="scss" scoped>
.details-header {
  background-size: cover;
  background-position: center center;
  height: 135px !important;
  .details-title {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    display: inline-flex;
    color: white;
    background: rgba(black, 0.5);
  }
}
.details-title {
  color: grey;
  font-size: 24px;
}
.details-data {
  color: grey;
  font-size: 18px;
}
.details-data-bold {
  font-weight: bold;
  color: grey;
  font-size: 18px;
}
::v-deep {
  .centered-alert .v-alert__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    & .v-alert__content {
      text-align: center;
    }
    & i {
      margin-right: auto;
      margin-left: auto;
    }
  }
}
</style>
